export type ArrayType<T> = T extends Array<infer U> ? U : never;
export type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any ? A : never;
export type PromiseType<T> = T extends Promise<infer U> ? U : never;
export type PatchPartial<T, K extends keyof T> = Partial<T> & Pick<T, K>;
export type ConditionPartial<T, C, Rest> = T extends C ? Partial<Rest> : Rest;
export type Unset<T = string> = T | undefined | null;

export type FixServerObject<T, K extends keyof T> = T &
  Required<{ [P in K]: Exclude<T[P], null | undefined> }>;
export type PickServerObject<T, K extends keyof T> = Pick<FixServerObject<T, K>, K>;

export type Nullable<T extends Record<any, any>> = {
  [P in keyof T]: T[P] | null;
};
export type PartialAndNullable<T> = Nullable<Partial<T>>;

export type Primitive = string | null | number | undefined | symbol | boolean;

export type Left<T, Allowed extends unknown = Primitive> = {
  [P in keyof T]: Extract<T[P], Allowed>;
};

/**
 * The shape react-hook-form is allowed to see for a model T: every key of T,
 * with primitive / Date / File / primitive-array members kept as they are and
 * every nested entity (object or entity array) opaque (`any`).
 *
 * Why (D-28): the generated API models carry the full EF navigation graph
 * (`Notebook -> userPatientProfileSessions[] -> UserPatientProfile -> ...`), and
 * react-hook-form 7 types `name` / `setValue` / `watch` with `Path<T>`, which
 * enumerates every dotted path through that graph: 47 s and TS2590 "union too
 * complex" for a single model. Through FormFields<T> the same model resolves
 * in 0.1 s. Apply it wherever a model enters RHF (`useForm<FormFields<T>>`,
 * `useFormContext<FormFields<T>>`, `ControllerRenderProps<FormFields<T>>`,
 * `IGridFormProps<T>` does it internally). Cf. `Left<T>`, which keeps only the
 * primitive part of each member; FormFields keeps the keys and their runtime
 * shape, it only hides the nesting from the path typing.
 */
export type FormFields<T> = {
  [K in keyof T]: [NonNullable<T[K]>] extends [never]
    ? any // a member Left<T> erased (e.g. `ids: string[]`) stays addressable by name
    : NonNullable<T[K]> extends Primitive | Date | File | FileList
    ? T[K]
    : NonNullable<T[K]> extends readonly (infer U)[]
    ? [U] extends [Primitive | Date]
      ? T[K]
      : any
    : any;
};

export const isSettledRejected = (
  input: PromiseSettledResult<unknown>,
): input is PromiseRejectedResult => input.status === 'rejected';

export const isSettledFulfilled = <T>(
  input: PromiseSettledResult<T>,
): input is PromiseFulfilledResult<T> => input.status === 'fulfilled';
